import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Alert, Box, Button, TextField } from '@mui/material';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { Loading } from '@bumble/app/components/Loading';

interface EditDataUsageResetDayProps {
  cpeId: string;
  onSubmit: () => void;
}

export const EditDataUsageResetDay = ({ cpeId, onSubmit }: EditDataUsageResetDayProps) => {
  const api = useApi();
  const [resetDay, setResetDay] = useState('');

  const { isLoading: loadingCpes, data: cpeData } = useQuery(
    ['cellularRouters'],
    api.getCpeCellularApiV1GetCpeCellularGet.bind(null, {}),
    { enabled: true },
  );

  const cpe = useMemo(
    () => cpeData?.data.find((item) => item.cpe_id === cpeId),
    [cpeData, cpeId],
  );
  const inDataPool = !!cpe?.data_pool_id;

  useEffect(() => {
    // Prefill with the CPE's current reset day once it is known.
    if (resetDay !== '' || loadingCpes) return;
    if (cpe) setResetDay(String(cpe.data_usage_reset_day ?? 1));
  }, [cpe, resetDay, loadingCpes]);

  const setResetDayMutation = useMutation(
    api.setCpeDataUsageResetDayApiV1SetCpeDataUsageResetDayPost,
    {
      onSuccess() {
        toast.success('Updated data usage reset day successfully.');
        onSubmit();
      },
      onError: apiError,
    },
  );

  // Same constraint as the backend: non-zero integer within [-28, 28].
  const parsed = Number(resetDay);
  const isValid =
    resetDay !== '' &&
    Number.isInteger(parsed) &&
    parsed !== 0 &&
    parsed >= -28 &&
    parsed <= 28;

  const handleSubmit = () => {
    if (!isValid) {
      toast.error('Reset day must be a non-zero whole number between -28 and 28.');
      return;
    }
    setResetDayMutation.mutate({ cpe_id: cpeId, reset_day: parsed });
  };

  if (loadingCpes) return <Loading />;

  return (
    <Box pt={2}>
      {inDataPool && (
        <Alert severity="info" sx={{ mb: 3 }}>
          This CPE is in a shared data pool, so its usage resets with the pool. This
          reset day applies once the CPE is removed from the pool.
        </Alert>
      )}
      <TextField
        fullWidth
        type="number"
        label="Data Usage Reset Day"
        variant="outlined"
        value={resetDay}
        onChange={(e) => setResetDay(e.target.value)}
        inputProps={{ min: -28, max: 28, step: 1 }}
        helperText="Day of month usage resets (1–28). Negative counts from end: -1 = last day."
      />
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || setResetDayMutation.isLoading}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};

EditDataUsageResetDay.displayName = 'Edit Data Usage Reset Day';
